import { useLocation, useNavigate } from "@tanstack/react-router";
import {
	CalendarCheck,
	ChevronDown,
	ChevronUp,
	FileSignature,
	LayoutDashboard,
	Link,
	Settings,
	Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { config as appConfig } from "@/config";
import { safeOpen } from "@/lib/safeOpen";
import { AmberFlexLogo } from "../../icons/amber-flex-logo";
import { CampaignsIcon } from "../../icons/campaigns-icon";
import { ChevronExpandIcon } from "../../icons/chevron-expand-icon";
import { CommissionsIcon } from "../../icons/commissions-icon";
import { ListingsIcon } from "../../icons/listings-icon";
import { NavLeftIcon } from "../../icons/nav-left";
import { NavRightIcon } from "../../icons/nav-right";
import { SubPartnersIcon } from "../../icons/sub-partners-icon";
import { SupportIcon } from "../../icons/support-icon";
import { partnerColors } from "../../theme";
import { type NavbarProps, type NavItem, showNavItem } from "./types";

export function Navbar({
	user,
	partnerConfig,
	onLogout,
	isCollapsed,
	onToggleCollapse,
}: NavbarProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const pathname = location.pathname;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
	const [collapsedSettingsOpen, setCollapsedSettingsOpen] = useState(false);

	// Get partner_id from current URL to preserve in navigation
	const searchParams = new URLSearchParams(
		typeof window !== "undefined" ? window.location.search : "",
	);
	const partnerIdFromUrl = searchParams.get("partner_id");

	// Default config if not provided (show all items)
	const config = partnerConfig ?? {
		navbarConfig: [
			"dashboard",
			"leads",
			"commissions",
			"contracts",
			"listings",
			"white_label_listings",
			"sub-partners",
			"campaigns",
			"integration",
			"support",
			"white_label_settings",
		],
		isSubPartner: false,
		isAuthorized: true,
		isBookingPartner: false,
		isPartnerCustomFormEnabled: true,
	};

	const {
		navbarConfig,
		isSubPartner,
		isAuthorized,
		isBookingPartner,
		isPartnerCustomFormEnabled,
		partnerName,
	} = config;

	// Display name: prefer partner name, fallback to user name
	const displayName = partnerName || user?.name || "Partner";
	const partnerUUID = user?.email || "";

	const handleNavigation = (href: string, internal?: boolean) => {
		if (internal) {
			// Preserve partner_id in URL for agent access flow
			if (partnerIdFromUrl) {
				// Use window.location for navigation to preserve partner_id
				window.location.href = `/flex${href}?partner_id=${partnerIdFromUrl}`;
			} else {
				navigate({ to: `/flex${href}` as string & {} });
			}
		} else {
			// External links - append partner_id as query param if present
			let url = href;
			if (partnerIdFromUrl) {
				const separator = href.includes("?") ? "&" : "?";
				url = `${href}${separator}partner_id=${partnerIdFromUrl}`;
			}
			safeOpen(url);
		}
	};

	// Build nav items with accessibility based on navbar_config
	const mainNavItems: NavItem[] = useMemo(
		() => [
			{
				title: "Dashboard",
				href: "/dashboard",
				icon: LayoutDashboard,
				internal: true,
				isActive:
					pathname.endsWith("/dashboard") && !pathname.includes("/dashboard/"),
				configKey: "dashboard",
				isAccessible: showNavItem("dashboard", navbarConfig),
			},
			{
				title: isBookingPartner ? "Bookings" : "Leads",
				href: "/dashboard/leads",
				icon: Users,
				internal: true,
				isActive: pathname.includes("/leads"),
				configKey: "leads",
				isAccessible: showNavItem("leads", navbarConfig),
			},
			{
				title: "Bookings",
				href: "/dashboard/bookings",
				icon: CalendarCheck,
				internal: true,
				isActive: pathname.includes("/bookings"),
				configKey: "leads",
				// Only visible for non-booking partners
				isAccessible: !isBookingPartner && showNavItem("leads", navbarConfig),
			},
			{
				title: "Commissions",
				href: "/dashboard/commissions",
				icon: CommissionsIcon,
				internal: true,
				isActive: pathname.includes("/commissions"),

				configKey: "commissions",
				// Commissions: not for sub-partners, requires admin role
				isAccessible:
					!isSubPartner &&
					isAuthorized &&
					showNavItem("commissions", navbarConfig),
			},
			{
				title: "Contracts",
				href: "/dashboard/contracts",
				icon: FileSignature,
				internal: true,
				isActive: pathname.includes("/contracts"),
				configKey: "contracts",
				// Always visible — static read-only page, no config dependency
				isAccessible: true,
			},
			{
				title: "Listings",
				href: "/dashboard/listings",
				icon: ListingsIcon,
				internal: true,
				isActive: pathname.includes("/listings"),
				configKey: ["listings", "white_label_listings"],
				// Either regular OR white label listings
				isAccessible:
					showNavItem("listings", navbarConfig) ||
					showNavItem("white_label_listings", navbarConfig),
			},
			{
				title: "Sub-Partners",
				href: "/dashboard/subpartners",
				icon: SubPartnersIcon,
				internal: true,
				isActive: pathname.includes("/subpartners"),
				configKey: "sub-partners",
				// Sub-partners cannot manage other sub-partners
				isAccessible:
					!isSubPartner && showNavItem("sub-partners", navbarConfig),
			},
			{
				title: "Campaigns / Events",
				href: "/dashboard/campaigns",
				icon: CampaignsIcon,
				internal: true,
				isActive: pathname.includes("/campaigns"),
				configKey: "campaigns",
				// Sub-partners cannot access campaigns
				isAccessible: !isSubPartner && showNavItem("campaigns", navbarConfig),
			},
		],
		[pathname, navbarConfig, isSubPartner, isAuthorized, isBookingPartner],
	);

	const systemNavItems: NavItem[] = useMemo(
		() => [
			{
				title: "Integrations",
				href: "/dashboard/integrations",
				icon: Link,
				internal: true,
				isActive: pathname.includes("/integrations"),
				configKey: "integration",
				isAccessible: showNavItem("integration", navbarConfig),
			},
			{
				title: "Support",
				href: "/dashboard/support",
				icon: SupportIcon,
				internal: true,
				isActive: pathname.includes("/support"),
				configKey: "support",
				isAccessible: showNavItem("support", navbarConfig),
			},
		],
		[pathname, navbarConfig],
	);

	// Settings sub-items with accessibility
	const settingsSubItems = useMemo(() => {
		const baseItems = [
			{
				title: "Site Configuration",
				href: "/dashboard/settings",
				isAccessible: showNavItem("white_label_settings", navbarConfig),
			},
		];

		// Conditionally add Custom Form if enabled
		if (isPartnerCustomFormEnabled) {
			baseItems.push({
				title: "Custom Form",
				href: "/dashboard/custom-form",
				isAccessible: showNavItem("white_label_settings", navbarConfig),
			});
		}

		baseItems.push({
			title: "Custom Page",
			href: "/dashboard/custom-page",
			isAccessible: showNavItem("white_label_settings", navbarConfig),
		});

		return baseItems.filter((item) => item.isAccessible);
	}, [navbarConfig, isPartnerCustomFormEnabled]);

	// Check if settings section should be visible
	const showSettings =
		showNavItem("white_label_settings", navbarConfig) &&
		settingsSubItems.length > 0;

	// Whether the current route is one of the settings sub-pages
	const isOnSettingsPage = useMemo(
		() =>
			settingsSubItems.some((item) => pathname.startsWith(`/flex${item.href}`)),
		[settingsSubItems, pathname],
	);

	// Keep the settings panel expanded whenever we're on one of its pages
	useEffect(() => {
		if (isOnSettingsPage) setSettingsOpen(true);
	}, [isOnSettingsPage]);

	// Filter to only accessible items
	const accessibleMainNavItems = mainNavItems.filter(
		(item) => item.isAccessible,
	);
	const accessibleSystemNavItems = systemNavItems.filter(
		(item) => item.isAccessible,
	);

	return (
		<>
			{/* Collapsed state - small expand button */}
			{isCollapsed && (
				<div className="w-16 min-w-[64px] max-w-[64px] h-screen flex flex-col items-center bg-[#fafafa] border-r border-gray-200 transition-all duration-300">
					{/* Expand button */}
					<div className="h-[56px] flex items-center justify-center border-b border-gray-200 w-full">
						<button
							type="button"
							onClick={onToggleCollapse}
							className="w-[28px] h-[28px] p-1.5 flex items-center justify-center cursor-pointer rounded-[var(--radius)] border border-[var(--input)] bg-[var(--background)] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
						>
							<NavRightIcon />
						</button>
					</div>

					{/* Nav icons */}
					<div className="flex flex-col items-center gap-2 py-3 w-full">
						{accessibleMainNavItems.map((item) => (
							<button
								type="button"
								key={item.title}
								onClick={() => handleNavigation(item.href, item.internal)}
								title={item.title}
								className={`w-10 h-10 flex items-center justify-center rounded-[var(--radius)] transition-colors ${
									item.isActive
										? "border border-[var(--border)] bg-[var(--background)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)]"
										: "text-gray-700 hover:bg-[#ececef]"
								}`}
							>
								<item.icon
									className={`w-4 h-4 ${
										item.isActive
											? partnerColors.text.blue[600]
											: "text-gray-700"
									}`}
								/>
							</button>
						))}
					</div>

					{/* System icons */}
					{accessibleSystemNavItems.length > 0 && (
						<div className="flex flex-col items-center gap-2 py-3 w-full border-t border-gray-200">
							{accessibleSystemNavItems.map((item) => (
								<button
									type="button"
									key={item.title}
									onClick={() => handleNavigation(item.href, item.internal)}
									title={item.title}
									className={`w-10 h-10 flex items-center justify-center rounded-[var(--radius)] transition-colors ${
										item.isActive
											? "border border-[var(--border)] bg-[var(--background)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)]"
											: "text-gray-700 hover:bg-[#ececef]"
									}`}
								>
									<item.icon
										className={`w-4 h-4 ${
											item.isActive
												? partnerColors.text.blue[600]
												: "text-gray-700"
										}`}
									/>
								</button>
							))}
							{showSettings && (
								<div className="relative">
									<button
										type="button"
										onClick={() =>
											setCollapsedSettingsOpen(!collapsedSettingsOpen)
										}
										title="Settings"
										className={`w-10 h-10 flex items-center justify-center rounded-[var(--radius)] transition-colors ${
											isOnSettingsPage
												? "border border-[var(--border)] bg-[var(--background)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1)]"
												: "text-gray-700 hover:bg-[#ececef]"
										}`}
									>
										<Settings
											className={`w-4 h-4 ${
												isOnSettingsPage
													? partnerColors.text.blue[600]
													: "text-gray-700"
											}`}
										/>
									</button>
									{collapsedSettingsOpen && (
										<>
											<button
												type="button"
												aria-label="Close settings"
												className="fixed inset-0 z-[9998] cursor-default bg-transparent border-0"
												onClick={() => setCollapsedSettingsOpen(false)}
											/>
											<div className="absolute left-full top-0 ml-2 z-[9999] w-48 p-1 rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white">
												{settingsSubItems.map((subItem) => (
													<button
														type="button"
														key={subItem.title}
														onClick={(e) => {
															e.stopPropagation();
															setCollapsedSettingsOpen(false);
															handleNavigation(subItem.href, true);
														}}
														className={`w-full h-9 flex items-center px-3 text-sm rounded-lg transition-colors ${
															pathname === `/flex${subItem.href}`
																? "bg-white text-black font-medium shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] border border-gray-200"
																: "text-gray-600 hover:bg-[#ececef] hover:text-black"
														}`}
													>
														{subItem.title}
													</button>
												))}
											</div>
										</>
									)}
								</div>
							)}
						</div>
					)}

					{/* Spacer */}
					<div className="flex-1" />

					{/* Footer with avatar */}
					<div className="w-full h-[76px] border-t border-gray-200 flex items-center justify-center relative">
						<button
							type="button"
							onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
							className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#ececef] bg-transparent border-0 cursor-pointer"
							title={displayName}
						>
							<Avatar className="h-10 w-10">
								<AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-medium">
									{displayName.charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>
						</button>
						{profileDropdownOpen && (
							<>
								<button
									type="button"
									aria-label="Close dropdown"
									className="fixed inset-0 z-[9998] cursor-default bg-transparent border-0"
									onClick={() => setProfileDropdownOpen(false)}
								/>
								<div className="absolute bottom-full left-2 mb-2 z-[9999] w-60 p-0 rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white">
									<div className="bg-gray-50 px-3 py-3">
										<div className="flex items-center gap-3">
											<Avatar className="h-10 w-10">
												<AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-medium">
													{displayName.charAt(0).toUpperCase()}
												</AvatarFallback>
											</Avatar>
											<div className="flex-1 min-w-0">
												<div className="text-sm font-semibold text-gray-900 truncate">
													{displayName}
												</div>
												{partnerUUID && (
													<div className="text-xs text-gray-500 truncate">
														{partnerUUID}
													</div>
												)}
											</div>
										</div>
									</div>
									<div className="h-px bg-gray-200" />
									<div className="py-1">
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setProfileDropdownOpen(false);
												handleNavigation("/dashboard/profile", true);
											}}
											className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
										>
											<svg
												className="h-4 w-4 text-gray-500"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<circle cx="12" cy="8" r="4" />
												<path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
											</svg>
											<span className="text-sm text-gray-900">
												Account Details
											</span>
										</button>
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setProfileDropdownOpen(false);
												handleNavigation("/dashboard/teams", true);
											}}
											className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
										>
											<svg
												className="h-4 w-4 text-gray-500"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
												<circle cx="9" cy="7" r="4" />
												<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
												<path d="M16 3.13a4 4 0 0 1 0 7.75" />
											</svg>
											<span className="text-sm text-gray-900">Teams</span>
										</button>
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setProfileDropdownOpen(false);
												safeOpen("https://amberstudent.com");
											}}
											className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
										>
											<svg
												className="h-4 w-4 text-gray-500"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
												<polyline points="15 3 21 3 21 9" />
												<line x1="10" y1="14" x2="21" y2="3" />
											</svg>
											<span className="text-sm text-gray-900">
												Visit amber student
											</span>
										</button>
									</div>
									<div className="h-px bg-gray-200" />
									<div className="py-1">
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setProfileDropdownOpen(false);
												onLogout?.();
											}}
											className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left text-red-500"
										>
											<svg
												className="h-4 w-4"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
												<polyline points="16 17 21 12 16 7" />
												<line x1="21" y1="12" x2="9" y2="12" />
											</svg>
											<span className="text-sm">Log Out</span>
										</button>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			)}

			{/* Full navbar */}
			<div
				className={`h-screen bg-[#fafafa] border-r border-gray-200 flex flex-col transition-all duration-300 overflow-hidden ${
					isCollapsed
						? "w-0 opacity-0"
						: "w-64 min-w-[256px] max-w-[256px] opacity-100"
				}`}
			>
				{/* Logo - China uses image logo, Global uses text logo */}
				<div className="h-[56px] border-b border-gray-200 px-3 py-2 box-border">
					<div className="w-[232px] h-[40px] flex items-center justify-between">
						<a href="/flex/dashboard" className="flex items-center">
							<div className="w-[110px] h-[20px]">
								{appConfig.global.isChinaApp ? (
									<img
										src={appConfig.logo.url}
										alt="Amber"
										className="w-full h-full"
									/>
								) : (
									<AmberFlexLogo
										className="w-full h-full"
										aria-label="amberflex logo"
									/>
								)}
							</div>
						</a>
						<button
							type="button"
							onClick={onToggleCollapse}
							className="w-[28px] h-[28px] p-1.5 flex items-center justify-center cursor-pointer rounded-[var(--radius)] border border-[var(--input)] bg-[var(--background)] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
						>
							<NavLeftIcon />
						</button>
					</div>
				</div>

				{/* Navigation */}
				<div className="w-full h-auto p-2 box-border">
					{accessibleMainNavItems.map((item) => (
						<button
							type="button"
							key={item.title}
							onClick={() => handleNavigation(item.href, item.internal)}
							className={`w-full h-9 flex items-center justify-start p-2 text-sm font-medium rounded-[var(--radius)] transition-colors ${
								item.isActive
									? "border border-[var(--border)] bg-[var(--background)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
									: "text-gray-700 hover:bg-[#ececef]"
							}`}
						>
							<item.icon
								className={`w-4 h-4 mr-3 flex-shrink-0 transition-colors ${
									item.isActive ? partnerColors.text.blue[600] : "text-gray-700"
								}`}
							/>
							<span
								className={`text-left text-sm truncate transition-all ${
									item.isActive ? "text-black font-medium" : "text-gray-700"
								}`}
							>
								{item.title}
							</span>
						</button>
					))}
				</div>

				{/* System Section - Only show if there are accessible items */}
				{(accessibleSystemNavItems.length > 0 || showSettings) && (
					<div className="w-full p-2 mt-2 box-border">
						<div className="px-2 mb-2">
							<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
								SYSTEM
							</h3>
						</div>
						{accessibleSystemNavItems.map((item) => (
							<button
								type="button"
								key={item.title}
								onClick={() => handleNavigation(item.href, item.internal)}
								className={`relative w-full h-9 flex items-center justify-start p-2 text-sm font-medium rounded-[var(--radius)] transition-colors ${
									item.isActive
										? "z-10 border border-[var(--border)] bg-[var(--background)] shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
										: "z-20 text-gray-700 hover:bg-[#ececef]"
								}`}
							>
								<item.icon
									className={`w-4 h-4 mr-3 flex-shrink-0 transition-colors ${
										item.isActive
											? partnerColors.text.blue[600]
											: "text-gray-700"
									}`}
								/>
								<span
									className={`text-left text-sm truncate transition-all ${
										item.isActive ? "text-black font-medium" : "text-gray-700"
									}`}
								>
									{item.title}
								</span>
							</button>
						))}

						{/* Settings with inline expandable sub-items */}
						{showSettings && (
							<>
								<button
									type="button"
									onClick={() => setSettingsOpen(!settingsOpen)}
									className={`relative w-full h-9 flex items-center justify-between p-2 text-sm font-medium rounded-[var(--radius)] transition-colors ${
										isOnSettingsPage || settingsOpen
											? "z-10"
											: "z-20 text-gray-700 hover:bg-[#ececef]"
									}`}
								>
									<div className="flex items-center">
										<Settings
											className={`w-4 h-4 mr-3 flex-shrink-0 transition-colors ${
												isOnSettingsPage || settingsOpen
													? partnerColors.text.blue[600]
													: "text-gray-700"
											}`}
										/>
										<span
											className={`text-left text-sm truncate transition-all ${
												isOnSettingsPage || settingsOpen
													? "text-black font-medium"
													: "text-gray-700"
											}`}
										>
											Settings
										</span>
									</div>
									{settingsOpen ? (
										<ChevronUp className="h-4 w-4" />
									) : (
										<ChevronDown className="h-4 w-4" />
									)}
								</button>

								{/* Settings sub-items */}
								{settingsOpen && (
									<div className="ml-2 border-l-2 border-blue-500 pl-0">
										{settingsSubItems.map((subItem) => (
											<button
												type="button"
												key={subItem.title}
												onClick={(e) => {
													e.stopPropagation();
													handleNavigation(subItem.href, true);
												}}
												className={`w-full h-9 flex items-center pl-8 text-sm rounded-lg transition-colors ${
													pathname === `/flex${subItem.href}`
														? "bg-white text-black font-medium shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] border border-gray-200"
														: "text-gray-600 hover:bg-[#ececef] hover:text-black"
												}`}
											>
												{subItem.title}
											</button>
										))}
									</div>
								)}
							</>
						)}
					</div>
				)}

				{/* Spacer to push footer to bottom */}
				<div className="flex-1" />

				{/* User Profile - Footer at bottom */}
				<div className="w-full h-[76px] p-2 border-t border-gray-200 relative box-border">
					<button
						type="button"
						onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
						className="w-full flex items-center justify-between p-2 h-auto hover:bg-[#ececef] rounded-lg bg-transparent border-0"
					>
						<div className="flex items-center space-x-3">
							<Avatar className="h-10 w-10">
								<AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-medium">
									{displayName.charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<div className="text-left min-w-0 flex-1">
								<div className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
									{displayName}
								</div>
								{partnerUUID && (
									<div className="text-sm text-gray-500 truncate max-w-[140px]">
										{partnerUUID}
									</div>
								)}
							</div>
						</div>
						<ChevronExpandIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
					</button>

					{profileDropdownOpen && (
						<>
							<button
								type="button"
								aria-label="Close dropdown"
								className="fixed inset-0 z-[9998] cursor-default bg-transparent border-0"
								onClick={() => setProfileDropdownOpen(false)}
							/>
							<div className="fixed bottom-2 left-2 z-[9999] w-60 p-0 rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white">
								{/* User Info Header */}
								<div className="bg-gray-50 px-3 py-3">
									<div className="flex items-center gap-3">
										<Avatar className="h-10 w-10">
											<AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-medium">
												{displayName.charAt(0).toUpperCase()}
											</AvatarFallback>
										</Avatar>
										<div className="flex-1 min-w-0">
											<div className="text-sm font-semibold text-gray-900 truncate">
												{displayName}
											</div>
											{partnerUUID && (
												<div className="text-xs text-gray-500 truncate">
													{partnerUUID}
												</div>
											)}
										</div>
									</div>
								</div>

								{/* Divider */}
								<div className="h-px bg-gray-200" />

								{/* Menu Items */}
								<div className="py-1">
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setProfileDropdownOpen(false);
											handleNavigation("/dashboard/profile", true);
										}}
										className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
									>
										<svg
											className="h-4 w-4 text-gray-500"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
											aria-hidden="true"
										>
											<circle cx="12" cy="8" r="4" />
											<path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
										</svg>
										<span className="text-sm text-gray-900">
											Account Details
										</span>
									</button>
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setProfileDropdownOpen(false);
											handleNavigation("/dashboard/teams", true);
										}}
										className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
									>
										<svg
											className="h-4 w-4 text-gray-500"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
											aria-hidden="true"
										>
											<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
											<circle cx="9" cy="7" r="4" />
											<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
											<path d="M16 3.13a4 4 0 0 1 0 7.75" />
										</svg>
										<span className="text-sm text-gray-900">Teams</span>
									</button>
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setProfileDropdownOpen(false);
											safeOpen("https://amberstudent.com");
										}}
										className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left"
									>
										<svg
											className="h-4 w-4 text-gray-500"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
											aria-hidden="true"
										>
											<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
											<polyline points="15 3 21 3 21 9" />
											<line x1="10" y1="14" x2="21" y2="3" />
										</svg>
										<span className="text-sm text-gray-900">
											Visit amber student
										</span>
									</button>
								</div>

								{/* Divider */}
								<div className="h-px bg-gray-200" />

								{/* Logout */}
								<div className="py-1">
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setProfileDropdownOpen(false);
											onLogout?.();
										}}
										className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#ececef] text-left text-red-500"
									>
										<svg
											className="h-4 w-4"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
											aria-hidden="true"
										>
											<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
											<polyline points="16 17 21 12 16 7" />
											<line x1="21" y1="12" x2="9" y2="12" />
										</svg>
										<span className="text-sm">Log Out</span>
									</button>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
}
